
export default {
  data() {
    return {
      videoVersion: 1,
      videoQuality: "540p",
      shouldLoadVideo: false,
      prefersReducedMotion: false,
      isInViewport: true,
      isPageHidden: false,
      videoObserver: null,
    };
  },

  computed: {
    posterUrl() {
      return `/video/splash-${this.videoVersion}-poster.webp`;
    },

    webmUrl() {
      return `/video/splash-${this.videoVersion}-${this.videoQuality}.webm`;
    },

    mp4Url() {
      return `/video/splash-${this.videoVersion}-${this.videoQuality}.mp4`;
    },
  },

  watch: {
    shouldLoadVideo(value) {
      if (!value || this.prefersReducedMotion) {
        return;
      }

      this.$nextTick(() => {
        this.setupVideoVisibilityObserver();
        this.playVideoIfAllowed();
      });
    },
  },

  mounted() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.prefersReducedMotion = true;
      return;
    }

    this.selectVideo();
    this.determineVideoQuality();
    this.scheduleVideoLoad();
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  },

  beforeDestroy() {
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );

    if (this.videoObserver) {
      this.videoObserver.disconnect();
      this.videoObserver = null;
    }

    this.pauseVideo();
  },

  methods: {
    setupVideoVisibilityObserver() {
      const videoElement = this.$refs.videoElement;
      if (!videoElement || !window.IntersectionObserver) {
        this.isInViewport = true;
        return;
      }

      if (this.videoObserver) {
        this.videoObserver.disconnect();
      }

      this.videoObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          this.isInViewport = entry.isIntersecting;

          if (this.isInViewport) {
            this.playVideoIfAllowed();
          } else {
            this.pauseVideo();
          }
        },
        {
          threshold: 0.2,
        }
      );

      this.videoObserver.observe(videoElement);
    },

    handleVisibilityChange() {
      this.isPageHidden = document.hidden;

      if (this.isPageHidden) {
        this.pauseVideo();
      } else {
        this.playVideoIfAllowed();
      }
    },

    playVideoIfAllowed() {
      if (
        !this.shouldLoadVideo ||
        this.prefersReducedMotion ||
        !this.isInViewport ||
        this.isPageHidden
      ) {
        return;
      }

      const videoElement = this.$refs.videoElement;
      if (!videoElement) {
        return;
      }

      const playPromise = videoElement.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {
          // Autoplay can be blocked by the browser in edge cases.
        });
      }
    },

    pauseVideo() {
      const videoElement = this.$refs.videoElement;
      if (!videoElement || videoElement.paused) {
        return;
      }

      videoElement.pause();
    },

    selectVideo() {
      const storageKey = "splash_video_selection";
      const stored = this.getStoredSelection(storageKey);

      if (stored && stored.expires > Date.now()) {
        this.videoVersion = stored.version;
      } else {
        this.videoVersion = Math.floor(Math.random() * 2) + 1;

        this.saveSelection(storageKey, {
          version: this.videoVersion,
          expires: Date.now() + 24 * 60 * 60 * 1000,
        });
      }
    },

    determineVideoQuality() {
      const viewport = window.innerWidth;
      const connection =
        navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection;

      if (connection?.saveData) {
        this.videoQuality = "360p";
        return;
      }

      if (connection?.effectiveType) {
        const type = connection.effectiveType;
        if (type === "slow-2g" || type === "2g") {
          this.videoQuality = "360p";
          return;
        }
        if (type === "3g") {
          this.videoQuality = "540p";
          return;
        }
        if (type === "4g") {
          if (viewport < 640) {
            this.videoQuality = "360p";
          } else {
            this.videoQuality = "540p";
          }
          return;
        }
      }

      if (viewport < 640) {
        this.videoQuality = "360p";
      } else if (viewport < 1280) {
        this.videoQuality = "540p";
      } else {
        this.videoQuality = "540p";
      }
    },

    scheduleVideoLoad() {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(
          () => {
            this.shouldLoadVideo = true;
          },
          { timeout: 1000 }
        );
      } else {
        if (document.readyState === "complete") {
          setTimeout(() => {
            this.shouldLoadVideo = true;
          }, 500);
        } else {
          window.addEventListener("load", () => {
            setTimeout(() => {
              this.shouldLoadVideo = true;
            }, 500);
          });
        }
      }
    },

    getStoredSelection(key) {
      try {
        const stored = localStorage.getItem(key);
        return stored ? JSON.parse(stored) : null;
      } catch (e) {
        return null;
      }
    },

    saveSelection(key, value) {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (e) {
        // Ignore write errors
      }
    },
  },
};
